import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { metricsService } from "../services/metrics-service";
import { MesOption } from "../types";

export const opsMetricsKeys = {
  all: ["ops-metrics"] as const,
  mes: (mes?: string) => [...opsMetricsKeys.all, mes ?? "recente"] as const,
};

// timeZone UTC é obrigatório aqui: a data é construída com Date.UTC(ano, mes-1, 1),
// ou seja, meia-noite UTC do dia 1. Sem fixar o fuso, o Intl formata no fuso local
// e em GMT-3 isso vira 21h do último dia do mês ANTERIOR — "2026-07" era exibido
// como "junho de 2026". Os dados sempre estiveram certos; só o rótulo atrasava um mês.
const formatador = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** "2026-07" → { value: "2026-07", label: "julho de 2026" } */
export function formatarMes(valor: string, aberto = false): MesOption {
  const [ano, mes] = valor.split("-").map(Number);
  const label = formatador.format(new Date(Date.UTC(ano, mes - 1, 1)));
  return { value: valor, label: aberto ? `${label} (em aberto)` : label, aberto };
}

export function useOpsMetrics() {
  // undefined = "o mês fechado mais recente", resolvido pelo backend. A lista de
  // meses vem junto da resposta, então o relógio que decide é o do servidor: a
  // versão anterior calculava a lista no cliente uma única vez na montagem do
  // componente, e uma aba aberta na virada do mês nunca via o mês novo.
  const [mes, setMes] = useState<string | undefined>(undefined);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: opsMetricsKeys.mes(mes),
    queryFn: async () => {
      const response = await metricsService.opsMetrics(mes ? { mes } : {});
      if (!response.success) throw new Error(response.message ?? "Erro ao carregar as métricas.");
      return response.data;
    },
    // A consulta é pesada e o número praticamente não se move dentro de uma
    // sessão — mesmo no mês em aberto, que anda de hora em hora e não de
    // segundo em segundo. Quem quiser o valor do minuto usa "Atualizar".
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const meses = useMemo(
    () => (data?.meses ?? []).map((m) => formatarMes(m, m === data?.mesAberto)),
    [data?.meses, data?.mesAberto]
  );

  return {
    meses,
    // Enquanto a primeira resposta não chega, não há mês a exibir no seletor.
    mes: mes ?? data?.periodo.mes,
    setMes,
    metrics: data,
    isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch,
  };
}
